import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import { resolveSeedanceDynamicProvider } from '@/ai/video/seedance/routing';
import {
  type AdminV2MediaMetaContext,
  type AdminV2MediaMetaSource,
  mergeAdminV2MediaMetaContext,
  readAdminV2MediaMetaValue,
} from './media-meta-context';
import { faceSwapComputedResolverDefinitions } from './runtime-registries/face-swap';
import { imageEditingComputedResolverDefinitions } from './runtime-registries/image-editing';
import {
  seedanceComputedResolverDefinitions,
  seedancePreflightValidatorDefinitions,
} from './runtime-registries/seedance';
import {
  upscalerComputedResolverDefinitions,
  upscalerMediaMetaTransformerDefinitions,
  upscalerPreflightValidatorDefinitions,
} from './runtime-registries/upscaler';
import type { AdminV2ToolRuntimePackage } from './tool-types';
import { composeAdminV2WorkspaceTemplatePicker } from './workspace-template-picker';

export { ADMIN_V2_FACE_SWAP_PROMPT } from './runtime-registries/face-swap';
export { ADMIN_V2_UPSCALER_EDIT_PROMPT } from './runtime-registries/upscaler';

export type AdminV2ComputedValueResolver = (params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  trustedExecutionContext?: AdminV2TrustedExecutionContext;
}) => unknown;

export type AdminV2TrustedExecutionContext = {
  hasPaidAccess: boolean;
};

export type AdminV2ComputedValueOutputType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'json';

export type AdminV2ComputedValueDefinition = {
  allowedInputFieldIds: string[];
  allowPreviousOutput: boolean;
  outputType: AdminV2ComputedValueOutputType;
  resolver: AdminV2ComputedValueResolver;
};

export type AdminV2ComputedResolverDefinition = {
  outputType: AdminV2ComputedValueOutputType;
  resolver: AdminV2ComputedValueResolver;
};

export type AdminV2RegisteredConditionResolver = (params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  executedNodeIds?: string[];
  providerErrorCode?: string;
  trustedExecutionContext?: AdminV2TrustedExecutionContext;
}) => boolean;

export type AdminV2RegisteredCreditCalculatorResolver = (params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds: string[];
  executedNodeIds?: string[];
}) => number;

export type AdminV2TemplateResolver = (params: {
  template?: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  runtimePackage?: AdminV2ToolRuntimePackage;
}) => unknown;

export type AdminV2MediaMetaResolver = (params: {
  fieldId: string;
  metaSource: AdminV2MediaMetaSource;
  metaPath: string[];
  mediaMetaContext?: AdminV2MediaMetaContext;
}) => unknown;

export type AdminV2PreflightValidator = (params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  providerInput: Record<string, unknown>;
  nodeId: string;
  modelUsageId: string;
  fieldId?: string;
  errorCode?: string;
}) => void;

export type AdminV2PreflightValidatorDefinition = {
  validator: AdminV2PreflightValidator;
};

export type AdminV2MediaMetaTransformer = (params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  outputSlots: Record<string, unknown>;
  nodeId: string;
  modelUsageId: string;
  targetFieldId?: string;
}) => AdminV2MediaMetaContext | undefined;

export type AdminV2MediaMetaTransformerDefinition = {
  transformer: AdminV2MediaMetaTransformer;
};

const computedValueDefinitions = {
  workspaceQuality: {
    allowedInputFieldIds: ['quality', 'scale'],
    allowPreviousOutput: false,
    outputType: 'string',
    resolver: ({ normalizedInput }) =>
      normalizedInput.quality ?? normalizedInput.scale ?? 'standard',
  },
  scale: {
    allowedInputFieldIds: ['scale'],
    allowPreviousOutput: false,
    outputType: 'string',
    resolver: ({ normalizedInput }) => normalizedInput.scale,
  },
} satisfies Record<string, AdminV2ComputedValueDefinition>;

const computedResolverDefinitions = {
  ...faceSwapComputedResolverDefinitions,
  ...imageEditingComputedResolverDefinitions,
  ...seedanceComputedResolverDefinitions,
  ...upscalerComputedResolverDefinitions,
} satisfies Record<string, AdminV2ComputedResolverDefinition>;

const registeredConditionResolvers = {
  hasPrimaryImage: ({
    normalizedInput,
  }: Parameters<AdminV2RegisteredConditionResolver>[0]) =>
    Object.values(normalizedInput).some(
      (value) => typeof value === 'string' && value.trim().length > 0
    ),
  hasPaidAccess: ({
    trustedExecutionContext,
  }: Parameters<AdminV2RegisteredConditionResolver>[0]) =>
    trustedExecutionContext?.hasPaidAccess === true,
  seedance20PreferReplicate: ({
    normalizedInput,
    mediaMetaContext,
  }: Parameters<AdminV2RegisteredConditionResolver>[0]) =>
    resolveSeedanceDynamicProvider({
      variant: '2.0',
      normalizedInput,
      mediaMetaContext,
    }) === 'replicate',
  seedance20FastPreferReplicate: ({
    normalizedInput,
    mediaMetaContext,
  }: Parameters<AdminV2RegisteredConditionResolver>[0]) =>
    resolveSeedanceDynamicProvider({
      variant: '2.0-fast',
      normalizedInput,
      mediaMetaContext,
    }) === 'replicate',
} satisfies Record<string, AdminV2RegisteredConditionResolver>;

const registeredCreditCalculatorResolvers = {
  submittedImageCount: ({ normalizedInput }) =>
    Object.values(normalizedInput).reduce<number>((sum, value) => {
      const isImageReference = (item: unknown) =>
        typeof item === 'string' &&
        item.trim().length > 0 &&
        (/^https?:\/\//.test(item.trim()) || item.trim().startsWith('/'));
      if (Array.isArray(value)) {
        return sum + value.filter(isImageReference).length;
      }
      return isImageReference(value) ? sum + 1 : sum;
    }, 0),
} satisfies Record<string, AdminV2RegisteredCreditCalculatorResolver>;

const templateResolverDefinitions = {
  stringTemplate: ({
    template,
    normalizedInput,
    previousOutputs,
  }: Parameters<AdminV2TemplateResolver>[0]) =>
    (template ?? '').replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_, key) => {
      const value =
        key in normalizedInput ? normalizedInput[key] : previousOutputs[key];
      return value === undefined || value === null ? '' : String(value);
    }),
  workspaceTemplatePrompt: ({
    runtimePackage,
    normalizedInput,
  }: Parameters<AdminV2TemplateResolver>[0]) => {
    if (!runtimePackage) {
      throw createAdminV2Error({
        code: 'ADMIN_V2_TOOL_TEMPLATE_PICKER_RUNTIME_MISSING',
        message:
          'Workspace template resolver requires the private Tool runtime package.',
        fieldPath: 'workspaceView.templatePicker',
        status: 409,
      });
    }
    return composeAdminV2WorkspaceTemplatePicker({
      runtimePackage,
      normalizedInput,
    }).prompt;
  },
  workspaceTemplateReferences: ({
    runtimePackage,
    normalizedInput,
  }: Parameters<AdminV2TemplateResolver>[0]) => {
    if (!runtimePackage) {
      throw createAdminV2Error({
        code: 'ADMIN_V2_TOOL_TEMPLATE_PICKER_RUNTIME_MISSING',
        message:
          'Workspace template resolver requires the private Tool runtime package.',
        fieldPath: 'workspaceView.templatePicker',
        status: 409,
      });
    }
    return composeAdminV2WorkspaceTemplatePicker({
      runtimePackage,
      normalizedInput,
    }).referenceImageUrls;
  },
} satisfies Record<string, AdminV2TemplateResolver>;

const mediaMetaResolverDefinitions = {
  readMediaMeta: (params: Parameters<AdminV2MediaMetaResolver>[0]) => {
    return readAdminV2MediaMetaValue({
      context: params.mediaMetaContext,
      fieldId: params.fieldId,
      metaSource: params.metaSource,
      metaPath: params.metaPath,
    });
  },
} satisfies Record<string, AdminV2MediaMetaResolver>;

function hasTransparentPixelsMeta(params: {
  mediaMetaContext?: AdminV2MediaMetaContext;
  fieldId: string;
}) {
  const metaSources: AdminV2MediaMetaSource[] = [
    'clientProvidedMeta',
    'providerReturnedMeta',
  ];
  return metaSources.some((metaSource) => {
    const value = readAdminV2MediaMetaValue({
      context: params.mediaMetaContext,
      fieldId: params.fieldId,
      metaSource,
      metaPath: ['hasTransparentPixels'],
    });
    return value === true;
  });
}

const preflightValidatorDefinitions = {
  ...seedancePreflightValidatorDefinitions,
  ...upscalerPreflightValidatorDefinitions,
  rejectTransparentImage: {
    validator: ({ mediaMetaContext, fieldId, errorCode }) => {
      const resolvedFieldId = fieldId ?? 'image';
      if (
        !hasTransparentPixelsMeta({
          mediaMetaContext,
          fieldId: resolvedFieldId,
        })
      ) {
        return;
      }
      throw createAdminV2Error({
        code: errorCode ?? 'ADMIN_V2_TOOL_TRANSPARENT_IMAGE_UNSUPPORTED',
        message: 'Transparent images are not supported for this tool.',
        fieldPath: `input.${resolvedFieldId}`,
        status: 422,
      });
    },
  },
} satisfies Record<string, AdminV2PreflightValidatorDefinition>;

const mediaMetaTransformerDefinitions = {
  ...upscalerMediaMetaTransformerDefinitions,
} satisfies Record<string, AdminV2MediaMetaTransformerDefinition>;

export type AdminV2ComputedValueKey = keyof typeof computedValueDefinitions;
export type AdminV2ComputedResolverKey =
  keyof typeof computedResolverDefinitions;
export type AdminV2RegisteredConditionKey =
  keyof typeof registeredConditionResolvers;
export type AdminV2RegisteredCreditCalculatorKey =
  keyof typeof registeredCreditCalculatorResolvers;
export type AdminV2TemplateResolverKey =
  keyof typeof templateResolverDefinitions;
export type AdminV2MediaMetaResolverKey =
  keyof typeof mediaMetaResolverDefinitions;
export type AdminV2PreflightValidatorKey =
  keyof typeof preflightValidatorDefinitions;
export type AdminV2MediaMetaTransformerKey =
  keyof typeof mediaMetaTransformerDefinitions;

export function isAdminV2ComputedValueKey(
  key: string
): key is AdminV2ComputedValueKey {
  return key in computedValueDefinitions;
}

export function isAdminV2ComputedResolverKey(
  key: string
): key is AdminV2ComputedResolverKey {
  return key in computedResolverDefinitions;
}

export function getAdminV2ComputedValueDefinition(
  key: string
): AdminV2ComputedValueDefinition | null {
  return isAdminV2ComputedValueKey(key) ? computedValueDefinitions[key] : null;
}

export function getAdminV2ComputedResolverDefinition(
  key: string
): AdminV2ComputedResolverDefinition | null {
  return isAdminV2ComputedResolverKey(key)
    ? computedResolverDefinitions[key]
    : null;
}

export function isAdminV2TemplateResolverKey(
  key: string
): key is AdminV2TemplateResolverKey {
  return key in templateResolverDefinitions;
}

export function isAdminV2MediaMetaResolverKey(
  key: string
): key is AdminV2MediaMetaResolverKey {
  return key in mediaMetaResolverDefinitions;
}

export function isAdminV2PreflightValidatorKey(
  key: string
): key is AdminV2PreflightValidatorKey {
  return key in preflightValidatorDefinitions;
}

export function isAdminV2MediaMetaTransformerKey(
  key: string
): key is AdminV2MediaMetaTransformerKey {
  return key in mediaMetaTransformerDefinitions;
}

function pickAllowedInput(params: {
  normalizedInput: Record<string, unknown>;
  allowedInputFieldIds: string[];
}) {
  return Object.fromEntries(
    params.allowedInputFieldIds
      .filter((fieldId) => Object.hasOwn(params.normalizedInput, fieldId))
      .map((fieldId) => [fieldId, params.normalizedInput[fieldId]])
  );
}

export function isAdminV2RegisteredConditionKey(
  key: string
): key is AdminV2RegisteredConditionKey {
  return key in registeredConditionResolvers;
}

export function isAdminV2RegisteredCreditCalculatorKey(
  key: string
): key is AdminV2RegisteredCreditCalculatorKey {
  return key in registeredCreditCalculatorResolvers;
}

export function resolveAdminV2ComputedValue(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
}) {
  if (!isAdminV2ComputedValueKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_COMPUTED_VALUE_UNREGISTERED',
      message: `Computed value ${params.key} is not registered in code.`,
      fieldPath: `computedValue.${params.key}`,
      status: 409,
    });
  }

  const definition = computedValueDefinitions[params.key];
  return definition.resolver({
    normalizedInput: pickAllowedInput({
      normalizedInput: params.normalizedInput,
      allowedInputFieldIds: definition.allowedInputFieldIds,
    }),
    mediaMetaContext: params.mediaMetaContext,
    previousOutputs: definition.allowPreviousOutput
      ? params.previousOutputs
      : {},
  });
}

export function resolveAdminV2ComputedResolver(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  trustedExecutionContext?: AdminV2TrustedExecutionContext;
}) {
  if (!isAdminV2ComputedResolverKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_COMPUTED_RESOLVER_UNREGISTERED',
      message: `Computed resolver ${params.key} is not registered in code.`,
      fieldPath: `computedResolver.${params.key}`,
      status: 409,
    });
  }

  return computedResolverDefinitions[params.key].resolver(params);
}

export function resolveAdminV2TemplateResolver(params: {
  key: string;
  template?: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  runtimePackage?: AdminV2ToolRuntimePackage;
}) {
  if (!isAdminV2TemplateResolverKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_TEMPLATE_RESOLVER_UNREGISTERED',
      message: `Template resolver ${params.key} is not registered in code.`,
      fieldPath: `templateResolver.${params.key}`,
      status: 409,
    });
  }

  return templateResolverDefinitions[params.key](params);
}

export function resolveAdminV2MediaMetaResolver(params: {
  key: string;
  fieldId: string;
  metaSource: AdminV2MediaMetaSource;
  metaPath: string[];
  mediaMetaContext?: AdminV2MediaMetaContext;
}) {
  if (!isAdminV2MediaMetaResolverKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_MEDIA_META_RESOLVER_UNREGISTERED',
      message: `Media metadata resolver ${params.key} is not registered in code.`,
      fieldPath: `mediaMetaResolver.${params.key}`,
      status: 409,
    });
  }

  return mediaMetaResolverDefinitions[params.key](params);
}

export function runAdminV2PreflightValidator(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  providerInput: Record<string, unknown>;
  nodeId: string;
  modelUsageId: string;
  fieldId?: string;
  errorCode?: string;
}) {
  if (!isAdminV2PreflightValidatorKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_PREFLIGHT_VALIDATOR_UNREGISTERED',
      message: `Preflight validator ${params.key} is not registered in code.`,
      fieldPath: `preflightValidator.${params.key}`,
      status: 409,
    });
  }

  return preflightValidatorDefinitions[params.key].validator(params);
}

export function resolveAdminV2MediaMetaTransformer(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  outputSlots: Record<string, unknown>;
  nodeId: string;
  modelUsageId: string;
  targetFieldId?: string;
}) {
  if (!isAdminV2MediaMetaTransformerKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_MEDIA_META_TRANSFORMER_UNREGISTERED',
      message: `Media metadata transformer ${params.key} is not registered in code.`,
      fieldPath: `mediaMetaTransformer.${params.key}`,
      status: 409,
    });
  }

  const transformed =
    mediaMetaTransformerDefinitions[params.key].transformer(params);
  return mergeAdminV2MediaMetaContext(params.mediaMetaContext, transformed);
}

export function resolveAdminV2RegisteredCondition(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  previousOutputs: Record<string, unknown>;
  executedNodeIds?: string[];
  providerErrorCode?: string;
  trustedExecutionContext?: AdminV2TrustedExecutionContext;
}) {
  if (!isAdminV2RegisteredConditionKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_CONDITION_UNREGISTERED',
      message: `Execution condition ${params.key} is not registered in code.`,
      fieldPath: `registeredCondition.${params.key}`,
      status: 409,
    });
  }

  return registeredConditionResolvers[params.key](params);
}

export function resolveAdminV2RegisteredCreditCalculator(params: {
  key: string;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds: string[];
  executedNodeIds?: string[];
}) {
  if (!isAdminV2RegisteredCreditCalculatorKey(params.key)) {
    throw createAdminV2Error({
      code: 'ADMIN_V2_TOOL_CREDIT_CALCULATOR_UNREGISTERED',
      message: `Credit calculator ${params.key} is not registered in code.`,
      fieldPath: `userCreditPolicy.registeredCalculator.${params.key}`,
      status: 409,
    });
  }

  return registeredCreditCalculatorResolvers[params.key](params);
}
